import type {
  DraftEntry,
  DraftSet,
  LoggerDraft,
  WeightUnit,
} from './loggerDraftTypes';

// Fail-toggle field set (AP-0p model). `fail` is a pure label; toggling it
// always resets the flash flags too. The rep decrement on a fail is a
// separate, explicit step (the fail-confirm popup writes `reps`), not part
// of this flag flip. Centralised so the three toggle sites (cycle_set
// reducer, onToggleFail, effort-commit clear) can't drift.
export const failToggleFields = (
  fail: boolean,
): Pick<DraftSet, 'fail' | 'prFlash' | 'regressionFlash'> => ({
  fail,
  prFlash: false,
  regressionFlash: false,
});

export const emptySet = (
  unit: WeightUnit = 'kg',
  loadedSides: 1 | 2 = 1,
): DraftSet => ({
  reps: '',
  weight: '',
  unit,
  extraNegative: false,
  completedAt: null,
  fail: false,
  prFlash: false,
  note: '',
  rpe: '',
  rir: '',
  loadedSides,
  duration: '',
  distanceM: '',
  avgHrBpm: '',
  calories: '',
  // A blank set carries neither half of AMRAP: no plan asked for it
  // (prescription) and nothing has been performed yet (outcome).
  amrapPrescribed: false,
  amrap: false,
  dropset: null,
});

export const initial = (): LoggerDraft => ({
  startedAt: new Date().toISOString(),
  entries: [],
});

export const replaceAt = <T>(arr: T[], i: number, next: T): T[] =>
  arr.map((it, idx) => (idx === i ? next : it));

export const removeAt = <T>(arr: T[], i: number): T[] =>
  arr.filter((_, idx) => idx !== i);

/** Apply a pure transform to the entry at `entryIdx`, preserving the
 *  out-of-range no-op (returns `state` unchanged) that every entry-
 *  level reducer case shares. Collapses the get-entry → guard →
 *  replaceAt boilerplate repeated across ~9 actions. */
export function withEntry(
  state: LoggerDraft,
  entryIdx: number,
  fn: (entry: DraftEntry) => DraftEntry,
): LoggerDraft {
  const entry = state.entries[entryIdx];
  if (!entry) return state;
  return {
    ...state,
    entries: replaceAt(state.entries, entryIdx, fn(entry)),
  };
}

/** Apply a pure transform to a single set, preserving the entry+set
 *  out-of-range no-ops. Collapses the nested replaceAt boilerplate
 *  shared by update_set / toggle_unit / toggle_sign / cycle_set. */
export function withSet(
  state: LoggerDraft,
  entryIdx: number,
  setIdx: number,
  fn: (set: DraftSet) => DraftSet,
): LoggerDraft {
  const entry = state.entries[entryIdx];
  if (!entry) return state;
  const set = entry.sets[setIdx];
  if (!set) return state;
  return {
    ...state,
    entries: replaceAt(state.entries, entryIdx, {
      ...entry,
      sets: replaceAt(entry.sets, setIdx, fn(set)),
    }),
  };
}

/** Build the "next set" seed for a given entry following the carry-
 *  forward rules of `add_set`: reps + weight carry from the last set
 *  (weight only when units match — pasting "190" from lb to kg row
 *  silently changes the physical load by 2.2×), unit resets to the
 *  user's preferred unit, completedAt clears, notes/RPE blank, cardio
 *  duration carries forward as a template. Extracted so `add_set` and
 *  the group-wide `add_round_to_group` share the same logic — when one
 *  needs adjusting, both inherit the fix. */
export function seedNextSet(
  entry: DraftEntry,
  defaultUnit: WeightUnit,
): DraftSet {
  const last = entry.sets.at(-1);
  if (!last) return emptySet(defaultUnit, entry.loadedSides);
  const carryWeight = last.unit === defaultUnit;
  return {
    reps: last.reps,
    weight: carryWeight ? last.weight : '',
    unit: defaultUnit,
    extraNegative: carryWeight ? last.extraNegative : false,
    completedAt: null,
    fail: false,
    prFlash: false,
    note: '',
    rpe: '',
    rir: '',
    loadedSides: entry.loadedSides,
    duration: entry.isCardio || entry.isTimeBased ? last.duration : '',
    distanceM: '',
    avgHrBpm: '',
    calories: '',
    // Neither half carries onto an extra set. The prescription stops at
    // the plan's last set — an off-plan set was never prescribed AMRAP —
    // and the outcome belongs to the performance about to happen, not to
    // the previous one. Same for the time axis (workPrescribed /
    // restPrescribedSec, HI-1): they are absent here by omission, and this
    // literal builds every field by hand rather than spreading `last`
    // precisely so a prescription can never leak forward by accident.
    amrapPrescribed: false,
    amrap: false,
    dropset: null,
  };
}
